import { jsx as _jsx } from "react/jsx-runtime";
import React, { useCallback, useMemo, useState } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { WEB3_ADDRESS } from '../wallet';
import { DEFAULT_DAPP_STATE, DEFAULT_CHAIN } from './constants';
import { defaultDAppToast } from './toast';
import { getChainState } from './utils/chain-state';
import { DAppStateContext } from './context/DAppStateContext';
import { useMultichainConnection } from './wallet/useMultichainConnection';
import { useChainData } from './chain-data/useChainData';
import { useOnChainActions } from './actions/useOnChainActions';
import { useSvmAdminActions } from './actions/useSvmAdminActions';
import { useEvmAdminActions } from './actions/useEvmAdminActions';
import { useTransactionHistory } from './transactions/useTransactionHistory';
import { useWalletAuth } from './wallet/useWalletAuth';
import { useProviderInit } from './effects/useProviderInit';
import { useEvmEffects } from './evm/useEvmEffects';
import { useSvmEffects } from './svm/useSvmEffects';
import { useFavoriteNfts } from './useFavoriteNfts';
import { useEthersSigner, useConnectedEthersSigner } from '../wallet/ethers-bridge';
import { useEVMBalance } from '../wallet/use-evm-balance';
import { ConnectionState } from '../wallet/types';
export const DAppStateProvider = ({ children, toast: toastApi = defaultDAppToast, onWalletConnected, onWalletDisconnected, createNftUris, walletSession, transactionHistory: transactionHistoryApi, }) => {
    const connection = useMultichainConnection({
        toast: toastApi,
        onWalletConnected,
        onWalletDisconnected,
        walletSession,
        onClearTransactionHistory: transactionHistoryApi?.clearAll,
    });
    const { favoriteNFTs, toggleFavoriteNFT } = useFavoriteNfts();
    const activeChain = connection.walletConnection.network ?? DEFAULT_CHAIN;
    const tx = useTransactionHistory({
        walletConnection: connection.walletConnection,
        toast: toastApi,
        transactionHistoryApi,
        walletSession,
    });
    const [state, setState] = useState(DEFAULT_DAPP_STATE);
    const activeState = useMemo(() => getChainState(state, activeChain), [activeChain, state]);
    const [walletAddresses, setWalletAddresses] = useState({
        evm: 'initial',
        svm: 'initial',
    });
    const [balances, setBalances] = useState({});
    const evmSigner = useEthersSigner({ chainId: WEB3_ADDRESS[activeChain]?.chainId });
    const evmWalletSigner = useConnectedEthersSigner();
    const evmBalanceInfo = useEVMBalance({
        chain: activeChain,
        address: connection.evmAddress,
        blockTag: 'pending',
    });
    const svmWallet = useWallet();
    const auth = useWalletAuth({
        walletConnection: connection.walletConnection,
        walletSession,
        toast: toastApi,
        handleDisconnect: connection.handleDisconnect,
        evmWalletSigner: evmWalletSigner ?? undefined,
        signMessage: connection.signMessage,
        publicKey: connection.publicKey,
        curEvmChainId: connection.curEvmChain?.id,
    });
    const chainData = useChainData({
        state,
        setState,
        setBalances,
        toast: toastApi,
        isWalletAuthenticated: auth.isWalletAuthenticated,
    });
    const actions = useOnChainActions({
        activeChain,
        activeState,
        evmSigner: evmSigner ?? undefined,
        toast: toastApi,
        addTransaction: tx.addTransaction,
        txIdToLink: tx.txIdToLink,
        createNftUris,
    });
    const refreshEvmGlobalState = useCallback(() => chainData.refreshEvmGlobalState(activeChain), [chainData.refreshEvmGlobalState, activeChain]);
    const svmAdmin = useSvmAdminActions({
        toast: toastApi,
        refreshSvmGlobalState: chainData.refreshSvmGlobalState,
    });
    const evmAdmin = useEvmAdminActions({
        activeChain,
        evmSigner: evmSigner ?? undefined,
        toast: toastApi,
        refreshEvmGlobalState,
    });
    const providerInit = useProviderInit({
        toast: toastApi,
        svmWallet,
        updateAllSVMChains: chainData.updateAllSVMChains,
    });
    useSvmEffects({
        initStatus: providerInit.initStatus,
        svmWallet,
        walletAddresses,
        setWalletAddresses,
        updateAllSVMChains: chainData.updateAllSVMChains,
        isWalletAuthenticated: auth.isWalletAuthenticated,
    });
    useEvmEffects({
        initStatus: providerInit.initStatus,
        activeChain,
        evmSigner: evmSigner ?? undefined,
        evmBalanceValue: evmBalanceInfo?.data?.value,
        balances,
        setBalances,
        walletAddresses,
        setWalletAddresses,
        incomingConnection: connection.incomingConnection,
        setIncomingConnection: connection.setIncomingConnection,
        updateAllEVMChains: chainData.updateAllEVMChains,
        updateChainState: chainData.updateChainState,
        isWalletAuthenticated: auth.isWalletAuthenticated,
    });
    const value = useMemo(() => ({
        isInitingStatus: providerInit.isInitingStatus,
        isWalletModalOpen: connection.isWalletModalOpen,
        setIsWalletModalOpen: connection.setIsWalletModalOpen,
        walletConnection: connection.walletConnection,
        setWalletConnection: connection.setWalletConnection,
        installedWallets: connection.installedWallets,
        ConnectionState,
        handleDisconnect: connection.handleDisconnect,
        switchNetwork: connection.switchNetwork,
        switchableNetwork: connection.switchableNetwork,
        state,
        activeState,
        ...actions,
        favoriteNFTs,
        toggleFavoriteNFT,
        history: tx.history,
        txHistory: tx.history,
        txHistoryStatus: tx.txHistoryStatus,
        trackTransaction: tx.trackTransaction,
        ...auth,
        ...svmAdmin,
        ...evmAdmin,
        refreshSvmGlobalState: chainData.refreshSvmGlobalState,
        refreshEvmGlobalState,
    }), [
        providerInit.isInitingStatus,
        connection,
        state,
        activeState,
        actions,
        favoriteNFTs,
        toggleFavoriteNFT,
        tx,
        auth,
        svmAdmin,
        evmAdmin,
        chainData.refreshSvmGlobalState,
        refreshEvmGlobalState,
    ]);
    return _jsx(DAppStateContext.Provider, { value: value, children: children });
};
export const useDAppState = () => React.useContext(DAppStateContext);
